import { useEffect } from "react";
import { Navigate, Outlet } from "react-router-dom";
import { createGlobalStyle } from "styled-components";
import { ThemedLayoutV2 } from "@refinedev/antd";
import Intercom, { shutdown } from "@intercom/messenger-js-sdk";
import { CustomSider, Title, MobileHeaderSpacer } from "components/layout";
import ErrorBoundary from "components/ErrorBoundary";
import { LoadingSpinner } from "components/LoadingSpinner";
import { useSession } from "hooks/useSession";

/**
 * Route layout for the playbook-centric "/create-experience" page.
 *
 * A focused alternative to the app-wide `Element` wrapper, built along the
 * same lines as `ExperienceV2Layout` — except that layout dropped the admin
 * sidebar in MED-6950 and this one still keeps it. It composes only the
 * chrome this page needs:
 *   - Auth gate via `useSession` (the app's own session hook)
 *   - Intercom (launcher hidden — the page is immersive)
 *   - Admin's left sidebar via `ThemedLayoutV2` + `CustomSider`
 *   - `ErrorBoundary`
 *
 * Auth is gated with `useSession` rather than Refine's `<Authenticated>`:
 * `<Authenticated>` runs its OWN `useIsAuthenticated` query, and wrapping
 * `ThemedLayoutV2` (whose `CustomSider` also reads session state) makes the
 * two auth subscriptions invalidate each other into a refetch loop. Using
 * the single `useSession` source — the same one `CustomSider` and the
 * app-wide `Element` use — avoids that.
 */
export const CreateExperienceLayout = () => {
  const { user, isAuthenticated, isLoading } = useSession();

  useEffect(() => {
    if (isLoading) {
      return; // avoid init intercom too early
    }
    let userProps = {};
    if (user && isAuthenticated) {
      const { id, first_name, last_name, email, created } = user;
      userProps = {
        user_id: String(id),
        name: `${first_name} ${last_name}`,
        email,
        created_at: created ? new Date(created).getTime() / 1000 : 0,
      };
    }
    Intercom({
      app_id: "mcwfcpeh",
      ...userProps,
      // Immersive page — keep the launcher hidden.
      hide_default_launcher: true,
    });

    return () => {
      shutdown();
    };
  }, [user, isAuthenticated, isLoading]);

  // Gate render until session loads — ThemedLayoutV2 / CustomSider call
  // useSession and would otherwise cascade re-renders (matches Element).
  if (isLoading) {
    return <LoadingSpinner tip="" />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  return (
    // `display: contents` keeps ThemedLayoutV2's flex layout intact while
    // giving the global style a scoping ancestor.
    <div className="create-experience-fullbleed" style={{ display: "contents" }}>
      <FullBleedContentStyle />
      <ThemedLayoutV2
        Title={Title}
        Sider={CustomSider}
        Header={MobileHeaderSpacer}
      >
        <ErrorBoundary id="create-experience">
          <div style={{ height: "100vh", overflow: "hidden" }}>
            <Outlet />
          </div>
        </ErrorBoundary>
      </ThemedLayoutV2>
    </div>
  );
};

// ─── Styled components ───

/**
 * Refine's ThemedLayoutV2 wraps page content in a
 * `<div style={{ minHeight: 360, padding: 24 }}>`. That inset is right for
 * standard CRUD pages but breaks this immersive page, which must be flush to
 * the chrome. Zero it out — scoped to this route via the wrapper class so no
 * other admin page is affected.
 */
const FullBleedContentStyle = createGlobalStyle`
  .create-experience-fullbleed .ant-layout-content > div {
    padding: 0 !important;
    min-height: 0 !important;
  }
`;
